import { useMemo } from "preact/compat"

import { ChevronDown } from "lucide-react"

import { DeductionListJson } from "@/api"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { StageMultiSelect, matching_stage_preset, stage_scope_label } from "@/deduction_table/stage_multi_select"
import { TableState } from "@/deduction_table/table_state"
import { ActiveFilterIcon } from "@/global_filter"
import { classes, currencyFormatter } from "@/utils"
import {
  AGE_BASIS_LABELS,
  AgeBasis,
  AgeBucket,
  AgeBucketFilterValue,
  BucketTally,
  CustomerAgingRow,
  STAGE_PRESETS,
  aging_summary,
  buckets_for_basis,
  is_age_bucket_filter_value,
  limit_customers,
} from "@/utils/deduction_age"
import { notifyChartFilterApplied, notifyChartFilterCleared } from "@/utils/filter_feedback"

import { AgingPanelState, effective_scope } from "./aging_state"

const MAX_MATRIX_CUSTOMERS = 8

interface AgingPanelProps {
  data: DeductionListJson[]
}

/**
 * Collapsible aging breakdown above the deductions table: 30/60/90-day buckets (count + $)
 * with a per-customer matrix, filterable by age basis and stage scope. Clicking a bucket
 * tile or matrix cell filters the table (same toast + scroll feedback as the other charts).
 */
export function AgingPanel({ data }: AgingPanelProps) {
  const state = AgingPanelState.use()
  const tableState = TableState.use()
  const basis = state.basis
  const scope = effective_scope(state)
  const summary = useMemo(() => aging_summary(data, basis, scope), [data, basis, scope])
  const buckets = buckets_for_basis(basis)
  const customer_rows = useMemo(() => limit_customers(summary.by_customer, MAX_MATRIX_CUSTOMERS, basis), [summary, basis])
  // Headline shown even while collapsed: deductions whose dispute window closes within
  // 30 days (actionable urgency), independent of the selected age basis.
  const expiring_soon = useMemo(
    () => aging_summary(data, "until_dispute_expiration", scope).buckets["expiring_30"],
    [data, scope]
  )

  const raw_filter = tableState.columnFilters.find(f => f.id === "days_old")?.value
  const active_value = is_age_bucket_filter_value(raw_filter) ? raw_filter : null
  const active_customer = tableState.columnFilters.find(f => f.id === "customer_name")?.value as string | undefined

  function is_bucket_active(bucket: AgeBucket, customer?: string): boolean {
    if (!active_value) return false
    if (active_value.basis !== basis || active_value.min !== bucket.min || active_value.max !== bucket.max) return false
    return active_customer === customer
  }

  function apply_bucket_filter(bucket: AgeBucket, customer?: string) {
    if (is_bucket_active(bucket, customer)) {
      TableState.set(s => ({
        ...s,
        columnFilters: s.columnFilters.filter(f => !["days_old", "status_value", "customer_name"].includes(f.id)),
        pagination: { pageIndex: 0, pageSize: s.pagination.pageSize },
      }))
      notifyChartFilterCleared(`Cleared the ${bucket.label} aging filter.`)
      return
    }
    const value: AgeBucketFilterValue = { basis, min: bucket.min, max: bucket.max }
    TableState.set(s => ({
      ...s,
      search: "",
      columnFilters: [
        { id: "days_old", value },
        ...(scope ? [{ id: "status_value", value: scope.join(",") }] : []),
        ...(customer ? [{ id: "customer_name", value: customer }] : []),
      ],
      pagination: { pageIndex: 0, pageSize: s.pagination.pageSize },
    }))
    const scope_label = state.custom_stages ? "selected stages" : STAGE_PRESETS[state.preset].label.toLowerCase()
    notifyChartFilterApplied(
      `Showing ${bucket.label} (${AGE_BASIS_LABELS[state.basis].toLowerCase()}) deductions in ${scope_label}${customer ? ` for ${customer}` : ""}.`
    )
  }

  return (
    <Collapsible open={state.open} onOpenChange={(open: boolean) => AgingPanelState.set(s => ({ ...s, open }))}>
      <Card className="mt-8">
        <CollapsibleTrigger className="w-full">
          <div class="flex items-center justify-between px-6 py-3">
            <div class="flex items-center gap-3">
              <span class="text-md font-medium">Deduction Aging</span>
              {expiring_soon.count > 0 && (
                <Badge variant="destructive" title="Dispute window closes within 30 days — file now or lose the money">
                  Expiring soon: {expiring_soon.count} · {currencyFormatter(expiring_soon.amount)}
                </Badge>
              )}
            </div>
            <ChevronDown className={classes("h-4 w-4 transition-transform", state.open ? "rotate-180" : "")} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-xs text-muted-foreground">Age by</span>
              <BasisSelect basis={state.basis} />
              <span class="ml-2 text-xs text-muted-foreground">Stages</span>
              <StageScopeSelect />
            </div>
            <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
              {buckets.map(bucket => (
                <BucketTile
                  key={bucket.key}
                  bucket={bucket}
                  tally={summary.buckets[bucket.key]}
                  active={is_bucket_active(bucket)}
                  onClick={() => apply_bucket_filter(bucket)}
                />
              ))}
            </div>
            <CustomerMatrix
              rows={customer_rows}
              buckets={buckets}
              is_cell_active={is_bucket_active}
              on_cell_click={apply_bucket_filter}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

function BasisSelect({ basis }: { basis: AgeBasis }) {
  return (
    <Select value={basis} onValueChange={(value: string) => AgingPanelState.set(s => ({ ...s, basis: value as AgeBasis }))}>
      <SelectTrigger className="h-8 w-52">
        <SelectValue>{AGE_BASIS_LABELS[basis]}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {Object.entries(AGE_BASIS_LABELS).map(([key, label]) => (
          <SelectItem key={key} value={key}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function on_stage_scope_change(stages: string[] | null) {
  const preset = matching_stage_preset(stages)
  if (preset) {
    AgingPanelState.set(s => ({ ...s, preset, custom_stages: null }))
  } else {
    AgingPanelState.set(s => ({ ...s, custom_stages: stages ?? [] }))
  }
}

function StageScopeSelect() {
  const state = AgingPanelState.use()
  const scope = effective_scope(state)
  const label = stage_scope_label(scope)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          {label}
          <ChevronDown className="ml-1 h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <StageMultiSelect selected={scope} onChange={on_stage_scope_change} />
      </PopoverContent>
    </Popover>
  )
}

interface BucketTileProps {
  bucket: AgeBucket
  tally: BucketTally
  active: boolean
  onClick: () => void
}

function BucketTile({ bucket, tally, active, onClick }: BucketTileProps) {
  return (
    <button
      class={classes(
        "rounded-lg border p-3 text-left transition-all duration-200 hover:shadow-md",
        bucket.emphasized ? "border-red-300 bg-red-50" : "bg-white",
        active ? "border-blue-400 shadow-md" : "hover:border-blue-300"
      )}
      onClick={onClick}>
      <div class="flex items-center gap-1 text-xs text-muted-foreground">
        {bucket.label}
        {active && <ActiveFilterIcon />}
      </div>
      <div class="text-xl font-semibold">{tally.count}</div>
      <div class="text-xs text-muted-foreground">{currencyFormatter(tally.amount)}</div>
    </button>
  )
}

interface CustomerMatrixProps {
  rows: CustomerAgingRow[]
  buckets: AgeBucket[]
  is_cell_active: (bucket: AgeBucket, customer?: string) => boolean
  on_cell_click: (bucket: AgeBucket, customer?: string) => void
}

function CustomerMatrix({ rows, buckets, is_cell_active, on_cell_click }: CustomerMatrixProps) {
  if (rows.length === 0) return null
  return (
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b text-xs text-muted-foreground">
            <th class="py-1 pr-2 text-left font-medium">Customer</th>
            {buckets.map(bucket => (
              <th key={bucket.key} class={classes("px-2 py-1 text-right font-medium", bucket.emphasized ? "text-red-700" : "")}>
                {bucket.label}
              </th>
            ))}
            <th class="py-1 pl-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.customer_name} class="border-b last:border-0">
              <td class="max-w-48 truncate py-1 pr-2" title={row.customer_name}>
                {row.customer_name}
              </td>
              {buckets.map(bucket => {
                const tally = row.buckets[bucket.key]
                // The "Other" rollup row isn't clickable — it has no single customer to filter by.
                const clickable = row.customer_name !== "Other" && tally.count > 0
                const active = is_cell_active(bucket, row.customer_name)
                const text = tally.count > 0 ? `${tally.count} · ${currencyFormatter(tally.amount)}` : "–"
                return (
                  <td key={bucket.key} class="px-2 py-1 text-right">
                    {clickable ? (
                      <button
                        class={classes(
                          "inline-flex items-center gap-1 rounded px-1 whitespace-nowrap hover:bg-blue-50 hover:text-blue-700",
                          bucket.emphasized && tally.count > 0 ? "font-medium text-red-700" : "",
                          active ? "bg-blue-50 text-blue-700" : ""
                        )}
                        onClick={() => on_cell_click(bucket, row.customer_name)}>
                        {text}
                        {active && <ActiveFilterIcon />}
                      </button>
                    ) : (
                      <span class="whitespace-nowrap text-muted-foreground">{text}</span>
                    )}
                  </td>
                )
              })}
              <td class="whitespace-nowrap py-1 pl-2 text-right font-medium">{currencyFormatter(row.total_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
